import { useState } from 'react';
import { useMutation } from '@apollo/client';

import {
    REGISTER_USER_MUTATION,
    RegisterUserInput,
    RegisterUserPayload
} from '../../../../queries/authentication/authentication';

import { UseFormMethods } from 'react-hook-form';

import RegistrationFormData from '../interfaces/RegistrationFormData';
import { triggerSuccessfulAuthentication } from '../utils/triggerSuccessfulAuthentication';

const errorCodes: { [key: string]: string } = {
    EMAIL_EXISTS: 'This email address is already in use',
    EMAIL_DELETED: 'This email address is already in use',
    USERNAME_EXISTS: 'That username already exists',
    PASSWORDS_DONT_MATCH: 'The passwords you entered do not match',
    BAD_EMAIL_FORMAT: 'The email address you entered is invalid',
    CONTACTNAME_MISSING_OR_INVALID: 'That contact name is missing or invalid',
    CONTAINS_PROFANITY: 'This username is not available',
    INCORRECT_EMAIL_PASSWORD:
        'Incorrect email or password. Please check and try again',
    GOOGLE_CLOSE_WINDOW_ERROR:
        'Something went wrong because the window was closed too soon. Please try again if you would like to continue.',
    GENERIC_SOCIAL_SIGN_IN_ERROR:
        'Something went wrong and you have not been signed in. Please try again.'
};

const useRegistration = (
    setError: UseFormMethods<RegistrationFormData>['setError'],
    onSuccess: () => void
) => {
    const [generalError, setGeneralError] = useState(false);

    const [registerMutation] = useMutation<
        { registerUser: RegisterUserPayload },
        { input: RegisterUserInput }
    >(REGISTER_USER_MUTATION, {
        onCompleted({ registerUser: response }) {
            if (response.errorField && response.errorCode) {
                const message =
                    errorCodes[response.errorCode] ??
                    'Sorry, something went wrong. Please try again.';

                setError(response.errorField as keyof RegistrationFormData, {
                    type: 'manual',
                    message,
                    shouldFocus: true
                });
                return;
            }
            if (!response.success) {
                setGeneralError(true);
                return;
            }
            triggerSuccessfulAuthentication();
            onSuccess();
        },
        onError() {
            setGeneralError(true);
        }
    });

    const registerUser = (props: RegistrationFormData) => {
        setGeneralError(false);

        const { email, password, situation, consent__insights__24 } = props;

        const input: RegisterUserInput = {
            email,
            password,
            propertyStatus: situation,
            consents: [
                {
                    consentName: 'consent.insights.24',
                    consentGiven: consent__insights__24
                }
            ]
        };

        return registerMutation({
            variables: {
                input
            }
        });
    };

    const resetGeneralError = () => {
        setGeneralError(false);
    };

    return {
        registerUser,
        generalError,
        resetGeneralError
    };
};

export default useRegistration;
